import type { FeatureKey } from './features';

/**
 * Organization names enum - add new organizations here
 * These should match the organization_name values from the JWT token
 */
export const Organization = {
  KAVKOM: 'Kavkom',
  EKSTEND: 'Ekstend',
  ADVANCED_RETAIL: 'Advanced Retail',
  RESTAURA: 'Restaura',
  MD2: 'MD2PG-ORG',
  CAPITAL_LYNK: 'Capital Lynk',
  ORCOM_US: 'Orcom US',
  NOVA_CAPITAL: 'Nova Capital',
  WAYFLYER: 'Wayflyer',
  QUINFICA: 'Quinfica',
  CROSS_RIVER_BANK: 'Cross River Bank',
  BLUEBERRIES: 'Blueberries.',
  OTSAR: 'otsar',
  CORGI: 'corgi',
  EMPORIUM: 'Emporium',
  EKOLE: 'EKOLE',
  GALYA: 'Galya',
  LYFE_SERVICE: 'LYFE Service Inc.',
  RHO: 'Rho',
  SHANI_WIGS: 'Shani Wigs',
} as const;

export type OrganizationName = (typeof Organization)[keyof typeof Organization];

/**
 * Display-only overrides for organization names. The KEYS here are the internal
 * identifiers (the JWT `organization_name` used everywhere for gating); the
 * VALUES are the labels shown to users. Use {@link getOrgDisplayName} at render
 * sites only — never in comparisons/gating, which must keep using the identifier.
 */
const ORG_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
  [Organization.NOVA_CAPITAL]: '770 Capital',
};

/** Friendly label to show for an org name. Falls back to the name itself. */
export function getOrgDisplayName(name: string | null | undefined): string {
  if (!name) return '';
  // The JWT says 'otsar', or the guard alias 'otsar-org'; the firm writes its
  // name 'Otsar', in French and in English.
  if (isOtsarOrganization(name)) return 'Otsar';
  return ORG_DISPLAY_NAME_OVERRIDES[name] ?? name;
}

/**
 * Maps curated/demo organizations to the feature keys they can access.
 * Orgs omitted here (e.g. Blueberries.) are treated as real orgs and get
 * default-allow for restricted features via {@link isPageAccessibleForOrg}.
 */
export const ORGANIZATION_PAGE_ACCESS: Partial<
  Record<OrganizationName, FeatureKey[]>
> = {
  [Organization.KAVKOM]: [
    'CSV_DATA_ENRICHMENT', // CSV Data Integration
    'FPA_KPIS_DASHBOARD', // KPI's Dashboard
    'FPA_TRANSACTIONS_OVERVIEW', // Clients Timeline
    'INTEGRATIONS', // All Integrations
  ],
  [Organization.EKSTEND]: [
    'FPA_BILLING_SIMULATOR', // Billing Simulator
  ],
  [Organization.ADVANCED_RETAIL]: [
    'FPA_ADVANCE_RETAIL', // Advance Retail
    'FPA_ADVANCE_RETAIL_II', // Advance Retail II
    'EXCEL_DATA_INTEGRATION', // Excel Data Integration
    'TIME_SPENT_BY_EMPLOYEES', // Time Spent by Employees
    'INTEGRATIONS',
    'TREASURY', // Treasury & Cash Flow
    'INVEST_CAPITAL', // Reports engine (Workspace → Reports)
  ],
  [Organization.RESTAURA]: [
    'INTEGRATIONS',
    'RESTAURA_DEMO',
    'INVEST_CAPITAL', // Reports engine (Workspace → Reports)
  ],
  [Organization.MD2]: [
    'INTEGRATIONS',
    'MD2PG',
    // Reports engine access (sidebar shows it as Workspace → Reports; the
    // Invest-Capital-branded FP&A entry stays hidden for this org).
    'INVEST_CAPITAL',
  ],
  [Organization.CAPITAL_LYNK]: [
    'CAPITAL_LYNK',
    'INVEST_CAPITAL', // Reports engine (Workspace → Reports)
  ],
  [Organization.WAYFLYER]: ['WAYFLYER'],
  [Organization.ORCOM_US]: [
    'DATA_PROCESSING_MARKETPLACE',
    'CLIENT_PNL_ECLIPSE',
  ],
  // Demo-only org: the Solutions/Analytics Studio showcase plus the trimmed
  // Integrations section (Excel Integration + All Integrations).
  [Organization.NOVA_CAPITAL]: [
    'SOLUTIONS_DEMO',
    'INTEGRATIONS',
    'EXCEL_INTEGRATION',
  ],
  // Real customer on the generic /workspace widget dashboard: its pages are
  // organization_pages (quinfica-overall/employees/data/attribution/emails),
  // served by the layout API — NOT FeatureKey sidebar sections. The list stays
  // minimal to preserve its pre-unification sidebar (it predates the self-serve
  // default-allow, so without this entry it would wrongly show every feature).
  // INVEST_CAPITAL = report-engine route access only; the sidebar shows it as
  // Workspace → Reports (the Invest-Capital-branded entry stays hidden).
  [Organization.QUINFICA]: ['INVEST_CAPITAL'],
  // Prospect org. It runs on the shipped screens: FPA_ADVANCE_RETAIL is
  // /financial, TIME_SPENT_BY_EMPLOYEES is /time-spent-by-employees, and
  // INTEGRATIONS is the connector catalogue. CROSS_RIVER_BANK covers the two
  // bespoke routes under /cross-river-bank that no shipped screen replaces
  // (driver configuration and the Pigment hand-off).
  //
  // Membership of this record is ALSO what exempts the org from the Stripe
  // payment gate (see usePaymentGate), so the key must stay even if the list
  // is ever emptied. Extend this array, never replace it.
  [Organization.CROSS_RIVER_BANK]: [
    'CROSS_RIVER_BANK',
    'FPA_ADVANCE_RETAIL',
    'TIME_SPENT_BY_EMPLOYEES',
    'INTEGRATIONS',
  ],
  // Connections home, plus Microsoft By Person on Workspace → Data Analysis,
  // plus the walkthrough on the firm's own figures under /otsar (OTSAR).
  // INTEGRATIONS keeps the Stripe payment-gate exemption (see usesConnectionsHome).
  // Membership of this record is that exemption: extend the array, never
  // replace it.
  [Organization.OTSAR]: ['INTEGRATIONS', 'MS_PERSON', 'OTSAR'],
  [Organization.CORGI]: ['INTEGRATIONS'],
  [Organization.EMPORIUM]: ['INTEGRATIONS'],
  [Organization.EKOLE]: ['INTEGRATIONS'],
  [Organization.GALYA]: ['INTEGRATIONS'],
  [Organization.LYFE_SERVICE]: ['INTEGRATIONS'],
  [Organization.RHO]: ['INTEGRATIONS'],
  [Organization.SHANI_WIGS]: ['INTEGRATIONS'],
};

export const CONNECTIONS_HOME_PATH = '/integrations/connections';

function normalizeOrgName(name: string): string {
  return name.trim().toLowerCase().replace(/\.+$/, '');
}

const CONNECTIONS_ONLY_ORG_NAMES = new Set<string>(
  [
    Organization.OTSAR,
    Organization.CORGI,
    Organization.EMPORIUM,
    Organization.EKOLE,
    Organization.GALYA,
    Organization.LYFE_SERVICE,
    Organization.RHO,
    Organization.SHANI_WIGS,
  ].map(normalizeOrgName)
);

/**
 * Orgs whose product is a dedicated screen, not the generic workspace and not
 * the Connections-only home. An empty layout must not replace their nav.
 */
const DEDICATED_PRODUCT_ORG_NAMES = new Set<string>(
  [
    Organization.RESTAURA,
    Organization.KAVKOM,
    Organization.ADVANCED_RETAIL,
    Organization.NOVA_CAPITAL,
    Organization.WAYFLYER,
    Organization.CROSS_RIVER_BANK,
    Organization.BLUEBERRIES,
  ].map(normalizeOrgName)
);

/** Otsar, including the Nest guard alias `otsar-org`. */
export function isOtsarOrganization(
  organizationName: string | null | undefined
): boolean {
  if (!organizationName) return false;
  const normalized = normalizeOrgName(organizationName);
  return normalized === 'otsar' || normalized === 'otsar-org';
}

/**
 * The language the Otsar walkthrough is on screen in, for the chrome outside
 * its route tree: today the sidebar's walkthrough group.
 *
 * The walkthrough's language lives in its own store
 * (`src/lib/showcase-data/language-store.ts`), which the sidebar must not
 * load: the sidebar is on every dashboard route, 770's and MD2PG's included,
 * and the Otsar shell stays out of their bundles (check-showcase-seam,
 * closures). Every route already loads this module, so the /otsar layout,
 * which reads the store, publishes the language here and the sidebar
 * subscribes with `useSyncExternalStore`. Nothing is persisted: until the
 * layout has published, a reader shows the firm's own default, French.
 */
export type OtsarWalkthroughLanguage = 'fr' | 'en';

let shownOtsarLanguage: OtsarWalkthroughLanguage | null = null;
const otsarLanguageListeners = new Set<() => void>();

/** Called by the /otsar layout whenever the walkthrough's language changes. */
export function publishOtsarWalkthroughLanguage(
  language: OtsarWalkthroughLanguage
): void {
  if (shownOtsarLanguage === language) return;
  shownOtsarLanguage = language;
  otsarLanguageListeners.forEach(listener => listener());
}

/** The language last published, or null before the walkthrough has opened. */
export function shownOtsarWalkthroughLanguage(): OtsarWalkthroughLanguage | null {
  return shownOtsarLanguage;
}

export function subscribeOtsarWalkthroughLanguage(
  listener: () => void
): () => void {
  otsarLanguageListeners.add(listener);
  return () => {
    otsarLanguageListeners.delete(listener);
  };
}

/**
 * Whether the build on screen holds Act 4, for the sidebar's Decide entry:
 * published by the /otsar layout like the language (a world built before Act
 * 4 shows the pending page, and the entry says it is in preparation). False
 * until the layout has published.
 */
let shownOtsarDecideBuilt = false;

/** Called by the /otsar layout whenever the build on screen changes. */
export function publishOtsarDecideBuilt(built: boolean): void {
  if (shownOtsarDecideBuilt === built) return;
  shownOtsarDecideBuilt = built;
  otsarLanguageListeners.forEach(listener => listener());
}

/** Whether the last build published holds Act 4. */
export function shownOtsarDecideIsBuilt(): boolean {
  return shownOtsarDecideBuilt;
}

/** Case-insensitive match against the JWT organization name. */
export function isConnectionsOnlyOrg(
  organizationName: string | null | undefined
): boolean {
  if (!organizationName) return false;
  return CONNECTIONS_ONLY_ORG_NAMES.has(normalizeOrgName(organizationName));
}

export function isInvestCapitalOrganization(
  organizationName: string | null | undefined
): boolean {
  return !!organizationName && /invest[\s_-]?capital/i.test(organizationName);
}

export function isDedicatedProductOrg(
  organizationName: string | null | undefined
): boolean {
  if (!organizationName) return false;
  if (isInvestCapitalOrganization(organizationName)) return true;
  return DEDICATED_PRODUCT_ORG_NAMES.has(normalizeOrgName(organizationName));
}

/** Map key for a JWT org name, ignoring case and a trailing period. */
export function curatedOrganizationKey(
  organizationName: string | null | undefined
): OrganizationName | undefined {
  if (!organizationName) return undefined;
  if (organizationName in ORGANIZATION_PAGE_ACCESS) {
    return organizationName as OrganizationName;
  }
  const normalized = normalizeOrgName(organizationName);
  return (Object.keys(ORGANIZATION_PAGE_ACCESS) as OrganizationName[]).find(
    key => normalizeOrgName(key) === normalized
  );
}

/**
 * True when this session should see the Otsar Connections home.
 * Named connections-only orgs always do. Any other org does once the layout
 * API has loaded and returned no workspace pages.
 */
export function usesConnectionsHome(
  organizationName: string | null | undefined,
  options?: {
    isAdminHome?: boolean;
    layout?: {
      pages?: readonly unknown[] | null;
      requiresOrgSelection?: boolean;
    } | null;
    layoutReady?: boolean;
  }
): boolean {
  if (options?.isAdminHome) return false;
  if (isConnectionsOnlyOrg(organizationName)) return true;
  if (!organizationName || !options?.layoutReady || !options.layout) {
    return false;
  }
  if (options.layout.requiresOrgSelection) return false;
  if (isDedicatedProductOrg(organizationName)) return false;
  return (options.layout.pages?.length ?? 0) === 0;
}

/**
 * Feature keys that have organization-based restrictions.
 * These are the only features that will use org-based access instead of the default logic.
 */
export const ORG_RESTRICTED_FEATURES: FeatureKey[] = [
  'CSV_DATA_ENRICHMENT',
  'FPA_KPIS_DASHBOARD',
  'FPA_TRANSACTIONS_OVERVIEW',
  'FPA_BILLING_SIMULATOR',
  'FPA_ADVANCE_RETAIL',
  'FPA_ADVANCE_RETAIL_II',
  'EXCEL_DATA_INTEGRATION',
  'EXCEL_INTEGRATION',
  'TIME_SPENT_BY_EMPLOYEES',
  'INTEGRATIONS',
  'TREASURY',
  'CAPITAL_LYNK',
  'WAYFLYER',
  'RESTAURA_DEMO',
  'DATA_PROCESSING_MARKETPLACE',
  'CLIENT_PNL_ECLIPSE',
  'MD2PG',
  'MS_PERSON',
  'OTSAR',
  'SOLUTIONS_DEMO',
  // Org-restricted so REAL (non-curated) orgs get it by default-allow: an Invest
  // Capital client org sees the product (payment gate handles the paywall), while
  // curated demo orgs stay locked out unless their list opts in and org-less
  // platform users keep the static-false default.
  'INVEST_CAPITAL',
  'CROSS_RIVER_BANK',
  // Internal-only: isPageAccessibleForOrg hard-limits them to the Blueberries org.
  'ENGINEERING',
  'SALES',
  'COLLABORATION',
  'TASKS',
];

/**
 * Org-restricted features a self-service (non-curated) organization may open.
 * Everything else in ORG_RESTRICTED_FEATURES is bound to a specific client's
 * data (Advanced Retail, Eclipse, MD2PG, Kavkom, ...) and stays locked.
 */
const SELF_SERVICE_ORG_FEATURES: FeatureKey[] = [
  'CSV_DATA_ENRICHMENT',
  'EXCEL_DATA_INTEGRATION',
  'EXCEL_INTEGRATION',
  'INTEGRATIONS',
  'INVEST_CAPITAL',
];

/** Portfolio management reports (the Blueberries × <org> deck) per org. */
export type PortfolioReportKey =
  | 'md2pg'
  | 'quinfica'
  | 'advanced-retail'
  | 'capital-lynk'
  | 'restaura'
  | 'github'
  | 'hubspot';

export function portfolioReportKey(
  organizationName: string | null | undefined
): PortfolioReportKey | null {
  // Internal org: the monthly Engineering report rides the same deck pipeline.
  if (organizationName === Organization.BLUEBERRIES) return 'github';
  if (organizationName === Organization.MD2) return 'md2pg';
  if (organizationName === Organization.QUINFICA) return 'quinfica';
  if (organizationName === Organization.ADVANCED_RETAIL)
    return 'advanced-retail';
  if (organizationName === Organization.CAPITAL_LYNK) return 'capital-lynk';
  if (organizationName === Organization.RESTAURA) return 'restaura';
  return null;
}

/**
 * Check if a feature is restricted by organization
 */
export function isOrgRestrictedFeature(featureKey: FeatureKey): boolean {
  return ORG_RESTRICTED_FEATURES.includes(featureKey);
}

/**
 * Check if a specific page/feature is accessible for a given organization.
 * Returns true if the organization has access to the feature.
 * Returns false if the organization does not have access or is not in our list.
 */
export function isPageAccessibleForOrg(
  organizationName: string | undefined | null,
  featureKey: FeatureKey
): boolean {
  if (!organizationName) {
    return false;
  }

  // Engineering/Sales/Collaboration analytics read Blueberries' own GitHub org,
  // HubSpot portal and Microsoft 365 tenant — internal only, never
  // default-allowed to client orgs.
  if (
    featureKey === 'ENGINEERING' ||
    featureKey === 'SALES' ||
    featureKey === 'COLLABORATION' ||
    featureKey === 'TASKS'
  ) {
    return organizationName === Organization.BLUEBERRIES;
  }

  // The sales showcase belongs to one organization and to no other.
  //
  // Without this it falls through to the default-allow below, so every
  // self-service organization can open /demo/* by typing the URL. That was
  // survivable while the showcase was a curiosity; it is not now that it ships
  // to production as the thing we hand to the sales team.
  //
  // Scoped deliberately to this one feature rather than fixing the
  // default-allow itself. Flipping that default would change the answer for
  // every organization not in the registry, on every page, which is a change
  // nobody asked for and one that cannot be reviewed in this diff.
  if (featureKey === 'SOLUTIONS_DEMO') {
    return organizationName === Organization.NOVA_CAPITAL;
  }

  // Outlook / Teams / calendar by person. Otsar only — including the
  // `otsar-org` alias the Nest guard accepts.
  if (featureKey === 'MS_PERSON') {
    return isOtsarOrganization(organizationName);
  }

  // The walkthrough under /otsar shows one client's real figures: Otsar only,
  // the `otsar-org` alias included. Its layout admits the Otsar token alone
  // (src/lib/showcase-data/access.ts); this keeps every other organization
  // off the route and out of the sidebar.
  if (featureKey === 'OTSAR') {
    return isOtsarOrganization(organizationName);
  }

  const orgKey = curatedOrganizationKey(organizationName);
  const orgAccess = orgKey ? ORGANIZATION_PAGE_ACCESS[orgKey] : undefined;
  if (!orgAccess) {
    // Not a curated org: a real self-service organization (e.g. one
    // auto-created when a user joins Blueberries Plus) only gets the
    // generic, org-scoped features. Client-specific screens read one
    // client's data and must never be default-allowed.
    return SELF_SERVICE_ORG_FEATURES.includes(featureKey);
  }

  return orgAccess.includes(featureKey);
}
